/**
 * Family placement validator — single source of truth for whether a relative
 * can be drawn on the visual family tree, based on the **parent-child link
 * graph** rather than stored display labels.
 *
 * RULES (from KinsCircle stabilization spec):
 *   1. Parent-child links are the primary source of truth.
 *   2. Display labels alone (cousin / sibling / aunt) are NEVER enough to
 *      decide tier or side placement.
 *   3. Validation must prove ONE of:
 *        - Member IS the viewer.
 *        - Member is viewer's spouse (via spouse_id, either direction).
 *        - Member's kinship code via the generational engine is one of the
 *          structurally-derivable codes (parent / child / sibling / ancestor
 *          / descendant / pibling / nibling / cousin).
 *        - Member's stored relationship is a "direct" type that has an
 *          unambiguous tier + side without needing a parent-child path
 *          (mother / father / step-parent / adopted-parent / grandparent /
 *          great-grandparent / sibling / spouse / child / aunt-uncle).
 *          NOTE: aunt-uncle is included here because their side
 *          (mother's/father's) is stored explicitly — they don't need a
 *          parent-link to render in the correct column. cousins and
 *          cousin-child are deliberately EXCLUDED from this fallback —
 *          they MUST prove their parent path to an aunt/uncle, otherwise
 *          they'd seat themselves on the sibling row by accident.
 *   4. When a member fails proof, callers should surface them as
 *      "Needs connection" in the roster — never silently place them.
 *
 * This module is pure / read-only — no DB writes, no schema changes.
 */
import {
  GraphMember,
  computeKinship,
} from '@/contexts/FamilyGraphContext';
import { getFamilyUnitAnchor, type SpousePartnerEdge } from '@/lib/familyUnits';


/**
 * Resolve the canonical viewer member from a list. Prefers an explicit
 * `viewerMemberId` (from FamilyGraphContext, which is set when the viewer
 * has claimed a profile in the loaded graph). Falls back to the legacy
 * heuristic of "first side='self' && relationship='unknown'" so existing
 * single-user trees keep working unchanged.
 */
export const resolveViewerAnchor = (
  members: GraphMember[],
  viewerMemberId: string | null
): GraphMember | null => {
  if (viewerMemberId) {
    const found = members.find((m) => m.id === viewerMemberId);
    return found ?? null;
  }

  return (
    members.find(
      (m) => m.side === 'self' && m.relationship === 'unknown'
    ) ?? null
  );
};

/**
 * Stored relationship types whose tier + side are unambiguous from the
 * stored fields alone. Members carrying one of these can render even when
 * their parent_ids are not yet linked (backward-compat for trees built
 * before the parent-link UI existed).
 *
 * Excluded on purpose: 'cousin', 'cousin-child', and 'unknown'. These
 * REQUIRE a parent-child path to position correctly:
 *  - cousins must sit beneath their aunt/uncle column, not on the sibling
 *    row;
 *  - cousin-children must sit beneath the cousin/sibling column, not as
 *    the user's own children;
 *  - 'unknown' has no defined tier or side at all.
 */
const DIRECT_PLACEMENT_TYPES = new Set([
  'mother',
  'father',
  'step-parent',
  'adopted-parent',
  'grandparent',
  'great-grandparent',
  'sibling',
  'spouse',
  'child',
  'aunt-uncle',
]);

/**
 * Returns true when we can prove `member` belongs at a specific tier +
 * side relative to `viewer`. This is the gate the visual tree should use
 * to decide whether to render `member` in a structured tier vs surface
 * them as "Needs connection" in the roster.
 *
 * The function is intentionally CONSERVATIVE — when in doubt, it returns
 * false so the roster can prompt the user to set up the missing parent
 * link. False NEVER hides the relative from the app: they remain in the
 * roster, in pending approvals, in their own profile, in memories, etc.
 *
 * ARCHITECTURAL NOTE — BLOODLINE vs FAMILY-UNIT (May 2026 pivot):
 *   This function delegates SPOUSE / PARTNER / IN-LAW placement proof
 *   to the FamilyUnit overlay engine in `src/lib/familyUnits.ts`. The
 *   bloodline path (kinship engine + parent_ids walk) NEVER requires a
 *   spouse_id link to succeed, and conversely the spouse path NEVER
 *   requires ancestry proof. If a person has a partner edge to ANY
 *   visible member of the tree (via spouse_id forward / reverse OR a
 *   spouse_partner_edges row), they ARE placeable, full stop. This is
 *   how son-/daughter-/sister-/brother-in-law and partner-of-an-ancestor
 *   render beside their anchor without ever appearing in Needs Connection.
 *
 * TRANSITIVE PARENT-LINK PROOF (spec rule: "Needs connection should clear
 * if parent_ids prove the path"):
 * If the member's own kinship-engine code is 'extended' / 'unknown' AND
 * their stored relationship isn't a direct-placement type, we ALSO walk
 * their parent_ids and accept the row as provable when ANY ancestor up
 * the chain is itself provable. This means:
 *   - A cousin saved with parent_ids = [aunt.id] is provable as soon as
 *     the aunt is provable (e.g. her stored relationship is 'aunt-uncle'),
 *     even when the aunt's own parent_ids haven't been linked to a
 *     grandparent yet.
 *   - A cousin's child with parent_ids = [cousin.id] is provable once
 *     the cousin is provable transitively up to the aunt.
 *   - A niece/nephew with parent_ids = [sibling.id] is provable as soon
 *     as the sibling is provable (kinship 'sibling' or stored 'sibling').
 *   - A grandchild with parent_ids = [child.id] is provable as soon as
 *     the child is provable.
 * Walks defensively with a cycle guard, so a malformed parent_ids loop
 * can never cause an infinite recursion.
 *
 * @param partnerEdges Optional spouse_partner_edges rows loaded by the
 *                     FamilyGraphContext. When provided, every edge that
 *                     touches `member` is treated as a valid placement
 *                     anchor — provided the OTHER side of the edge is
 *                     itself provable. Omitting this argument keeps the
 *                     legacy spouse_id-only behavior, which is what every
 *                     pre-pivot caller still does (zero-impact default).
 */
export const isPlacementProvable = (
  member: GraphMember,
  viewer: GraphMember | null,
  members: GraphMember[],
  visited?: Set<string>,
  partnerEdges?: SpousePartnerEdge[],
): boolean => {
  if (!viewer) {
    // No anchor yet — nothing is provable. Caller should treat the whole
    // graph as needing setup (this only happens before the user creates
    // their first self-anchor row).
    return false;
  }
  if (member.id === viewer.id) return true;

  // Spouse via spouse_id (either direction) to the VIEWER specifically.
  if (
    (viewer.spouseId && viewer.spouseId === member.id) ||
    (member.spouseId && member.spouseId === viewer.id)
  ) {
    return true;
  }

  // ---- SPOUSE/PARTNER EDGE TO ANY VISIBLE FAMILY MEMBER ----
  // FamilyUnit overlay rule (May 2026 architectural pivot, verbatim):
  //   "If a person has ANY valid spouse/partner connection to a visible
  //    family member: they are connected, they are NOT Needs connection,
  //    they must render beside the spouse anchor."
  //
  // This block accepts BOTH legacy spouse_id (forward + reverse) AND the
  // new spouse_partner_edges table rows. Either is sufficient. Bloodline
  // proof is never required for partner placement.
  //
  // Cycle guard: if we're already inside a recursive walk (visited set
  // exists), don't re-enter the spouse branch — the parent_ids walk will
  // handle deeper proofs and we don't want a spouse-of-a-spouse infinite
  // bounce. The seed call has visited === undefined, so the first hop
  // through a partner edge IS taken.
  if (!visited) {
    // Forward spouse_id.
    if (member.spouseId) {
      const spousePartner = members.find((mm) => mm.id === member.spouseId);
      if (
        spousePartner &&
        spousePartner.id !== member.id &&
        spousePartner.approvalStatus !== 'rejected' &&
        isPlacementProvable(
          spousePartner,
          viewer,
          members,
          new Set([member.id]),
          partnerEdges,
        )
      ) {
        return true;
      }
    }
    // Reverse spouse_id — someone else stored spouse_id pointing at us.
    const reverseSpouse = members.find(
      (mm) =>
        mm.id !== member.id &&
        mm.spouseId === member.id &&
        mm.approvalStatus !== 'rejected',
    );
    if (
      reverseSpouse &&
      isPlacementProvable(
        reverseSpouse,
        viewer,
        members,
        new Set([member.id]),
        partnerEdges,
      )
    ) {
      return true;
    }
    // spouse_partner_edges — every row that touches this member.
    if (partnerEdges && partnerEdges.length > 0) {
      for (const e of partnerEdges) {
        let partnerId: string | null = null;
        if (e.personAId === member.id) partnerId = e.personBId;
        else if (e.personBId === member.id) partnerId = e.personAId;
        if (!partnerId || partnerId === member.id) continue;
        const partnerRow = members.find((mm) => mm.id === partnerId);
        if (!partnerRow) continue;
        if (partnerRow.approvalStatus === 'rejected') continue;
        if (
          isPlacementProvable(
            partnerRow,
            viewer,
            members,
            new Set([member.id]),
            partnerEdges,
          )
        ) {
          return true;
        }
      }
    }
  }

  // Generational engine — walks parent_ids both ways looking for the
  // closest common ancestor or direct lineage. Returns a structured code
  // for any provable relationship; 'extended' / 'unknown' mean "no
  // parent-child path could be established".
  const k = computeKinship(members, viewer.id, member.id);
  switch (k.code) {
    case 'self':
    case 'spouse':
    case 'parent':
    case 'child':
    case 'sibling':
    case 'ancestor':
    case 'descendant':
    case 'pibling':
    case 'nibling':
    case 'cousin':
      return true;
    case 'extended':
    case 'unknown':
    default:
      // Fall through to the stored-label backward-compat check below.
      break;
  }

  // Backward-compat: the member's stored relationship is a "direct" type
  // whose tier + side don't require a parent-link to render correctly.
  // This keeps trees built before linkParents existed from breaking.
  if (DIRECT_PLACEMENT_TYPES.has(member.relationship)) {
    return true;
  }

  // ---- TRANSITIVE PARENT_IDS PROOF ----
  // Spec rule (verbatim): "Needs connection should clear if parent_ids
  // prove the path." If the kinship engine couldn't classify this row
  // directly, but the row HAS parent_ids pointing at a member who IS
  // provable, we accept this row as provable too. Cycle-guarded via the
  // `visited` set so a malformed parent_ids loop (e.g. A.parent=B,
  // B.parent=A) can never hang the renderer.
  const parentIds = (member.parentIds ?? []).filter(Boolean);
  if (parentIds.length > 0) {
    const seen = visited ?? new Set<string>();
    if (seen.has(member.id)) return false;
    seen.add(member.id);
    for (const pid of parentIds) {
      if (pid === viewer.id) {
        // Direct child of the viewer — always provable.
        return true;
      }
      if (seen.has(pid)) continue;
      const parent = members.find((mm) => mm.id === pid);
      if (!parent) continue;
      if (isPlacementProvable(parent, viewer, members, seen, partnerEdges)) {
        return true;
      }
    }
  }

  return false;
};


/**
 * Convenience — compute the set of member ids that the visual tree
 * cannot prove placement for, given the viewer anchor. Used by the
 * dashboard to populate the "Needs connection" tab in the roster.
 *
 * Always excludes:
 *  - Rejected members (they're hidden site-wide).
 *  - The canonical YOU anchor (it's drawn as the YOU card).
 *
 * @param partnerEdges Optional spouse_partner_edges rows. Forwarded to
 *                     `isPlacementProvable` so partner-edge rows clear
 *                     Needs Connection independent of ancestry proof.
 *                     When omitted, behavior matches the legacy spouse_id-
 *                     only proof path.
 */
export const computeUnplacedIds = (
  members: GraphMember[],
  viewer: GraphMember | null,
  partnerEdges?: SpousePartnerEdge[],
): Set<string> => {
  const unplaced = new Set<string>();
  if (!viewer) {
    for (const m of members) {
      if (m.approvalStatus === 'rejected') continue;
      unplaced.add(m.id);
    }
    return unplaced;
  }
  // ── FINAL FORCE FIX (May 2026) — Family-Unit anchor short-circuit ──
  // Build the visible-anchor set FIRST, then for any not-yet-placed
  // member ask getFamilyUnitAnchor whether they have a spouse / partner
  // / in-law connection to a visible member. If they do, the spec
  // mandates they NEVER appear in Needs Connection. This is the
  // single source-of-truth gate at the data-contract level — every
  // consumer (dashboard roster, debug panel, future UIs) inherits it
  // automatically by calling computeUnplacedIds.
  const visibleAnchorIds = new Set<string>();
  for (const m of members) {
    if (m.approvalStatus === 'rejected') continue;
    if (m.id === viewer.id) {
      visibleAnchorIds.add(m.id);
      continue;
    }
    if (isPlacementProvable(m, viewer, members, undefined, partnerEdges)) {
      visibleAnchorIds.add(m.id);
    }
  }
  for (const m of members) {
    if (m.approvalStatus === 'rejected') continue;
    if (m.id === viewer.id) continue;
    if (visibleAnchorIds.has(m.id)) continue;
    // Family-Unit override — partner of any visible bloodline anchor.
    const anchor = getFamilyUnitAnchor(
      m,
      members,
      partnerEdges,
      (id) => visibleAnchorIds.has(id),
    );
    if (anchor && anchor.shouldExcludeFromNeedsConnection) continue;
    unplaced.add(m.id);
  }
  return unplaced;
};



